import { Component, DestroyRef, EventEmitter, Input, Output, inject, signal } from '@angular/core';
import { EntryService } from '../entry.service';
import { Store } from '@ngrx/store';
import { UtilService } from 'src/app/util.service';
import { EntryData, TedDomain } from '../structure-data.models';
import { GoogleAnalyticsService } from 'src/app/google-analytics.service';
import { PlatformService } from 'src/app/platform.service';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { EntrySelectors } from 'src/app/state/entry.selectors';
import { combineLatest, filter, map, tap } from 'rxjs';
const COUNT_LEGENDS = 12;

@Component({
  selector: 'app-legends-panel',
  templateUrl: './legends-panel.component.html',
  styleUrl: './legends-panel.component.css',
  standalone: false
})
export class LegendsPanelComponent {
  @Output() toggleAccordion: EventEmitter<Object> = new EventEmitter();
  @Output() toggleAccordionSelection: EventEmitter<Object> = new EventEmitter();
  @Output() openedAccordionEmit: EventEmitter<Object> = new EventEmitter();

  private store = inject(Store);
  accession = toSignal(this.store.select(EntrySelectors.accession));
  public entryData = signal<EntryData>({} as EntryData);
  public tedDomainData = signal<TedDomain[]>([]);
  public csvAmData = signal<string>('');
  public macromoleculesData = signal<EntryData[]>([]);
  public allMacromoleculesData = signal<EntryData[]>([]);
  private readonly destroyRef = inject(DestroyRef);
  public slicedTedDomain = signal<TedDomain[]>([]);
  public showMoreLegends = signal<boolean>(false);
  public legendText = signal<string>('');
  public colorscheme : string = 'plddt';
  public selectedMacromoleculeIndex: number | null = null;
  public hasMacromoleculeSelection: boolean = false;
  private lastMacromoleculesKey?: string;
  public domainCount : number = 0;
  constructor(
    public gaService: GoogleAnalyticsService,
    private entryService: EntryService,
    public platform: PlatformService,
    public readonly utilService: UtilService
    ) {}

  public domainData = this.entryService.domainData();

  // uniprotAccession alone isn't a reliable "one macromolecule" key: a polyprotein cleaved
  // into separate chains (e.g. NS3/NS5) can report the SAME accession for genuinely
  // different chains with different residue ranges (AFPDBE-1435). Deduping by accession
  // alone (as this used to) silently drops one of them from the accordion. Group by the
  // shared composite key instead — see UtilService.groupMacromoleculeChains — and keep one
  // representative per group, in first-occurrence order (matching the old dedup's ordering).
  private getUniqueMacromoleculesByAccession(macromolecules: EntryData[]): EntryData[] {
    return this.utilService.groupMacromoleculeChains(macromolecules).map((group) => group[0]);
  }

  private getUniqueChainsForMacromoleculeByAccession(macromolecule: EntryData, allMacromolecules: EntryData[]): string[] {
    const accession = (macromolecule as any)?.uniprotAccession;
    const chainId = (macromolecule as any)?.chainId as string | undefined;

    if (!accession) {
      return chainId ? [chainId] : [];
    }

    // Only the chains in `macromolecule`'s own group (same accession+description+start), not
    // every chain that merely shares its accession — otherwise clicking e.g. NS3 would also
    // select/zoom NS5's chains, since they share an accession (AFPDBE-1435).
    const group = this.utilService.groupMacromoleculeChains(allMacromolecules).find((candidate) => candidate.includes(macromolecule)) || [];

    const seen = new Set<string>();
    const uniqueChains: string[] = [];
    for (const item of group) {
      const itemChainId = (item as any)?.chainId as string | undefined;
      if (itemChainId && !seen.has(itemChainId)) {
        seen.add(itemChainId);
        uniqueChains.push(itemChainId);
      }
    }

    uniqueChains.sort((a, b) => a.localeCompare(b));
    return uniqueChains;
  }

  private getAllUniqueChains(allMacromolecules: EntryData[]): string[] {
    const seen = new Set<string>();
    const uniqueChains: string[] = [];

    for (const item of allMacromolecules) {
      const chainId = ((item as any)?.chainId as string | undefined)?.trim();
      if (chainId && !seen.has(chainId)) {
        seen.add(chainId);
        uniqueChains.push(chainId);
      }
    }

    uniqueChains.sort((a, b) => a.localeCompare(b));
    return uniqueChains;
  }

  private sortMacromoleculesByPrimaryChain(macromolecules: EntryData[], allMacromolecules: EntryData[]): EntryData[] {
    return [...macromolecules].sort((a, b) => {
      const chainA = this.getUniqueChainsForMacromoleculeByAccession(a, allMacromolecules)[0] || '';
      const chainB = this.getUniqueChainsForMacromoleculeByAccession(b, allMacromolecules)[0] || '';

      if (chainA && chainB && chainA !== chainB) {
        return chainA.localeCompare(chainB);
      }

      if (chainA && !chainB) return -1;
      if (!chainA && chainB) return 1;

      const nameA = ((a as any)?.uniprotDescription as string | undefined) || '';
      const nameB = ((b as any)?.uniprotDescription as string | undefined) || '';
      return nameA.localeCompare(nameB);
    });
  }

  ngOnInit(): void {
    combineLatest([
      this.store.select(EntrySelectors.entryData).pipe(filter(Boolean)),
      this.store.select(EntrySelectors.tedDomainData).pipe(filter(Boolean)),
      this.store.select(EntrySelectors.csvAmData).pipe(),
      this.store.select(EntrySelectors.macromoleculesData).pipe()
    ])
      .pipe(
        map(async ([entryData, tedDomainData, csvAmData, macromoleculesData]) => {
          this.entryData.set(entryData)
          this.tedDomainData.set(tedDomainData)
          this.csvAmData.set(csvAmData ?? '')
          const macromolecules = macromoleculesData || [];
          this.allMacromoleculesData.set(macromolecules);
          const uniqueMacromolecules = this.getUniqueMacromoleculesByAccession(macromolecules);
          this.macromoleculesData.set(this.sortMacromoleculesByPrimaryChain(uniqueMacromolecules, macromolecules))
          // This fires again whenever TED/CSV data arrives; only reset the user's selection
          // when the macromolecule list itself actually changed.
          const macromoleculesKey = macromolecules.map((m: any) => `${m.uniprotAccession}|${m.chainId}|${m.uniprotStart ?? m.sequenceStart}`).join(',');
          if (macromoleculesKey !== this.lastMacromoleculesKey) {
            this.lastMacromoleculesKey = macromoleculesKey;
            this.selectedMacromoleculeIndex = null;
            this.hasMacromoleculeSelection = false;
          }
          this.domainCount = this.tedDomainData() ? this.tedDomainData().length : 0;
          this.slicedTedDomain.update(() =>
            this.tedDomainData()?.slice(0, COUNT_LEGENDS)
          );
          this.legendText.set(`Show all ${this.tedDomainData()?.length} domains`);
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }
  
  viewerLegend: any = [
    { style: { backgroundColor: '#0053D6' }, label: 'Very high (pLDDT > 90)' },
    { style: { backgroundColor: '#65CBF3' }, label: 'High (90 > pLDDT > 70)' },
    { style: { backgroundColor: '#FFDB13' }, label: 'Low (70 > pLDDT > 50)' },
    { style: { backgroundColor: '#FF7D45' }, label: 'Very low (pLDDT < 50)' },
  ];

  alphamissenseLegends: any = [
    { style: { backgroundColor: '#2166ac' }, label: 'Likely benign' },
    { style: { backgroundColor: '#A8A9AC' }, label: 'Uncertain' },
    { style: { backgroundColor: '#b2182b' }, label: 'Likely pathogenic' },
  ];

  onOpenLegendsPanel(type: string): void {
    this.openedAccordionEmit.emit({ type });
    
    const shouldEmit = type === 'plddt' || 
                       (type === 'missense' && this.entryData().amAnnotationsUrl) ||
                       (type === 'ted' && this.tedDomainData()?.length > 0);
  
    if (shouldEmit) {
      this.toggleAccordion.emit({ type });
    }

    if (type !== 'macromolecules') {
      this.selectedMacromoleculeIndex = null;
      this.hasMacromoleculeSelection = false;
    }

    this.colorscheme = type;

    //for GA tags
    let tag = "";
    let gtagmsg = "";
    if(type === "plddt"){
      tag = "model_confidence_accordion";
      gtagmsg = "Clicks to expand/collapse Model Confidence legend";
    }
    if(type === "missense"){
      tag = "domains_accordion";
      gtagmsg = "Clicks to expand/collapse Domains accordion";
    }
    if(type === "ted"){
      tag = "annotations_accordion";
      gtagmsg = "Clicks to expand/collapse 'Annotations' accordion";
    }
    this.gaService.eventEmitter('accordion_toggle', 'accordion_expand', 'click', tag, gtagmsg);
  }

  public clearMacromoleculeSelection(emit?: boolean): void {
    this.selectedMacromoleculeIndex = null;
    this.hasMacromoleculeSelection = false;
    if (emit) this.toggleAccordionSelection.emit({ type: 'macromolecules', selections: [] });
  }

  onMacromoleculeClick(index: number): void {
    // If the same macromolecule is clicked again, deselect it and clear the selection in Mol*.
    if (this.selectedMacromoleculeIndex === index) {
      this.selectedMacromoleculeIndex = null;
      this.hasMacromoleculeSelection = false;
      this.toggleAccordionSelection.emit({ type: 'macromolecules', selections: [] });
      return;
    }

    // Select the new macromolecule and prepare the selection data for Mol*.
    this.selectedMacromoleculeIndex = index;
    const selectedMacromolecule = this.macromoleculesData()[index];
    const allMacromolecules = this.allMacromoleculesData();

    // Get the unique chains for the selected macromolecule based on its accession.
    const uniqueChains = this.getUniqueChainsForMacromoleculeByAccession(selectedMacromolecule, allMacromolecules);
    const allChainsInEntry = this.getAllUniqueChains(allMacromolecules);

    // Generate the selection data for Mol* based on the unique chains. Each selection includes the entity_id and auth_asym_id (chainId).
    const molstarSelectionsByChain = uniqueChains.map(chainId => {
      const otherChainIds = allChainsInEntry.filter(id => id !== chainId);
      return {
        zoom: [{
          auth_asym_id: chainId,
        }],
        select: {
          data: [...otherChainIds.map(id => ({ auth_asym_id: id, color: null, opacity: 0.4 }))],
        },
      };
    });

    // Emit the selection data to the parent component, which will then pass it to the Mol* viewer to highlight the selected chains.
    this.toggleAccordionSelection.emit({ type: 'macromolecules', selections: molstarSelectionsByChain, selectionNames: uniqueChains.map((id) => `Chain ${id}`) });
    this.hasMacromoleculeSelection = true;
  }
}
